/**
 * Compare API Service
 *
 * Handles compare workflow configuration and document role management
 */

import { apiClient } from './client';
import {
  APIResponse,
  DocumentRoleAssignment,
  DocumentRoleBulkAssignment,
  DocumentRolesResponse,
  DocumentRoleValidationResponse,
  DocumentRoleAssignmentResult,
  DocumentRoleBulkAssignmentResult
} from '../../types';

class CompareAPI {
  // Compare Workflow Configuration Methods for Task 71
  async configureCompareWorkflow(projectId: string, config: any): Promise<any> {
    const response = await apiClient.post(
      `/api/v1/projects/${projectId}/configure/compare`,
      config
    );
    return response.data;
  }

  async getCompareConfiguration(projectId: string): Promise<any> {
    try {
      // Tell axios that 404 is acceptable - prevents error logging for unconfigured projects
      const response = await apiClient.get(
        `/api/v1/projects/${projectId}/configure/compare`,
        {
          validateStatus: (status) => status < 400 || status === 404
        }
      );

      // If 404, configuration doesn't exist yet (expected for new projects)
      if (response.status === 404) {
        throw new Error('Configuration not found');
      }

      return response.data;
    } catch (error: any) {
      // If it's our "Configuration not found" error, re-throw it
      if (error.message === 'Configuration not found') {
        throw error;
      }
      // For other errors, check if there's a detail message
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  async updateCompareConfiguration(projectId: string, config: any): Promise<any> {
    const response = await apiClient.put(
      `/api/v1/projects/${projectId}/configure/compare`,
      config
    );
    return response.data;
  }

  async validateCompareConfiguration(projectId: string): Promise<APIResponse> {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/configure/compare/validate`);
    return response.data;
  }

  async getDefaultCompareConfiguration(projectId: string): Promise<any> {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/configure/compare/default`);
    return response.data;
  }

  // Document Role Management Methods for Task 70
  async assignDocumentRole(
    projectId: string,
    documentId: string,
    roleAssignment: DocumentRoleAssignment
  ): Promise<DocumentRoleAssignmentResult> {
    const response = await apiClient.post(
      `/api/v1/projects/${projectId}/documents/${documentId}/role`,
      roleAssignment
    );
    return response.data;
  }

  async bulkAssignDocumentRoles(
    projectId: string,
    bulkAssignment: DocumentRoleBulkAssignment
  ): Promise<DocumentRoleBulkAssignmentResult> {
    const response = await apiClient.put(
      `/api/v1/projects/${projectId}/documents/roles`,
      bulkAssignment
    );
    return response.data;
  }

  async getProjectDocumentRoles(projectId: string): Promise<DocumentRolesResponse> {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/documents/roles`);
    return response.data;
  }

  async removeDocumentRole(projectId: string, documentId: string): Promise<APIResponse> {
    const response = await apiClient.delete(
      `/api/v1/projects/${projectId}/documents/${documentId}/role`
    );
    return response.data;
  }

  async validateProjectDocumentRoles(projectId: string): Promise<DocumentRoleValidationResponse> {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/documents/roles/validate`);
    return response.data;
  }
}

export const compareApi = new CompareAPI();
